import React, { useEffect, useMemo, useState } from "react";
import {
  Activity,
  ArrowLeft,
  Brain,
  Calendar,
  ChevronDown,
  Clock3,
  CircleHelp,
  Heart,
  Leaf,
  Plus,
  TrendingUp,
  TrendingDown,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import useMetrics from "../hooks/useMetrics";
import { useAuth } from "../contexts/AuthContext";
import { useVitals } from "../contexts/VitalsContext";
import { deriveQureoScore, getQureoScoreLabel } from "../lib/qureoScore";
import { onHealthDataUpdated } from "../lib/healthSync";

const RING_DOTS = 52;
const PRIMARY_COLOR = "var(--primary)";
const PRIMARY_SOFT_COLOR = "#e5ddff";

function DottedScoreRing({ score }) {
  const safe = Number.isFinite(Number(score)) ? Number(score) : 0;
  const activeDots = Math.round((safe / 100) * RING_DOTS);

  return (
    <div
      style={{
        width: "100%",
        height: 210,
        margin: "0 auto",
        position: "relative",
      }}
    >
      {Array.from({ length: RING_DOTS }).map((_, i) => {
        const angle = (i / RING_DOTS) * Math.PI * 2;
        const radius = 91;
        const x = 105 + Math.cos(angle) * radius;
        const y = 105 + Math.sin(angle) * radius;
        return (
          <span
            key={i}
            style={{
              position: "absolute",
              left: x,
              top: y,
              width: i < activeDots ? 4 : 3,
              height: i < activeDots ? 4 : 3,
              borderRadius: "50%",
              background: i < activeDots ? PRIMARY_COLOR : PRIMARY_SOFT_COLOR,
              transform: "translate(-50%, -50%)",
            }}
          />
        );
      })}

      <div
        style={{
          position: "absolute",
          inset: 40,
          borderRadius: "50%",
          border: `2px solid ${PRIMARY_COLOR}`,
          background: "#f3f4f6",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          color: PRIMARY_COLOR,
        }}
      >
        <div style={{ fontSize: 40, fontWeight: 700, lineHeight: 1 }}>{safe}</div>
        <div style={{ fontSize: 11, color: "#838f97", marginTop: 3 }}>out of 100</div>

        
      </div>
    </div>
  );
}

const average = (values = []) => {
  const valid = values.filter((v) => Number.isFinite(v));
  if (!valid.length) return null;
  return valid.reduce((sum, v) => sum + v, 0) / valid.length;
};

const formatRelativeTime = (input) => {
  if (!input) return "No recent update";
  const date = new Date(input);
  if (Number.isNaN(date.getTime())) return "No recent update";

  const diffMs = Date.now() - date.getTime();
  const minutes = Math.floor(diffMs / (1000 * 60));
  if (minutes < 1) return "Updated just now";
  if (minutes < 60) return `Updated ${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `Updated ${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `Updated ${days}d ago`;
};

const buildChartPath = (points = []) => {
  if (!points.length) {
    return {
      linePath: "M0,56 L320,56",
      areaPath: "M0,56 L320,56 L320,76 L0,76 Z",
    };
  }

  const width = 320;
  const height = 76;
  const min = Math.min(...points, 0);
  const max = Math.max(...points, 100);
  const range = Math.max(1, max - min);

  const coords = points.map((p, i) => {
    const x = points.length === 1 ? 0 : (i / (points.length - 1)) * width;
    const y = height - (((p - min) / range) * (height - 12) + 6);
    return { x, y };
  });

  let linePath = `M${coords[0].x},${coords[0].y}`;
  for (let i = 1; i < coords.length; i += 1) {
    const prev = coords[i - 1];
    const curr = coords[i];
    const cpX = (prev.x + curr.x) / 2;
    linePath += ` C${cpX},${prev.y} ${cpX},${curr.y} ${curr.x},${curr.y}`;
  }

  const last = coords[coords.length - 1];
  const areaPath = `${linePath} L${last.x},${height} L${coords[0].x},${height} Z`;

  return { linePath, areaPath };
};

const getOverviewRows = (components) => {
  const vitals = Number.isFinite(components?.vitals) ? components.vitals : null;
  const lifestyle = Number.isFinite(components?.lifestyle) ? components.lifestyle : null;
  const sleep = Number.isFinite(components?.medicationAdherence) ? components.medicationAdherence : null;

  return [
    {
      title: "Physical Health",
      subtitle: "Optimal physical health",
      status: vitals != null && vitals >= 75 ? "Excellent" : "Average",
      statusColor: vitals != null && vitals >= 75 ? PRIMARY_COLOR : "#f59e0b",
      icon: <Plus size={16} color="#1f2937" />,
    },
    {
      title: "Mental Health",
      subtitle: "Needs a few improvement",
      status: lifestyle != null && lifestyle >= 65 ? "Good" : "Bad",
      statusColor: lifestyle != null && lifestyle >= 65 ? PRIMARY_COLOR : "#ef4444",
      icon: <Brain size={16} color="#1f2937" />,
    },
    {
      title: "Nutrition",
      subtitle: "You are on track",
      status: vitals != null && vitals >= 60 ? "Good" : "Average",
      statusColor: vitals != null && vitals >= 60 ? PRIMARY_COLOR : "#f59e0b",
      icon: <Leaf size={16} color="#1f2937" />,
    },
    {
      title: "Activity Level",
      subtitle: "2 out of 5 activity this week",
      status: "Decent",
      statusColor: "#f59e0b",
      icon: <Activity size={16} color="#1f2937" />,
    },
    {
      title: "Sleep Level",
      subtitle: "Critical improvement needed",
      status: sleep != null && sleep >= 65 ? "Good" : "Insomnia",
      statusColor: sleep != null && sleep >= 65 ? PRIMARY_COLOR : "#ef4444",
      icon: <span style={{ fontSize: 15, fontWeight: 700, color: "#1f2937", letterSpacing: -0.3 }}>Zz</span>,
    },
  ];
};

const HealthDashboardSections = () => {
  const navigate = useNavigate();
  const { fetchHealthSummary, fetchVitalsMetrics, metrics, getCachedHealthSummary } = useMetrics();
  const { assessments } = useAuth();
  const { vitals } = useVitals();
  const [summary, setSummary] = useState(null);
  const [timeRange, setTimeRange] = useState("weekly");
  const [trendPoints, setTrendPoints] = useState([]);
  const [trendDelta, setTrendDelta] = useState(0);

  useEffect(() => {
    let mounted = true;

    const cached = getCachedHealthSummary();
    if (cached) {
      setSummary(cached);
    }

    const loadSummary = async () => {
      try {
        const data = await fetchHealthSummary({ forceRefresh: true });
        if (mounted) setSummary(data || null);
      } catch {
        if (!mounted) return;
        const fallback = getCachedHealthSummary();
        setSummary(fallback || null);
      }
    };

    loadSummary();
    const unsubscribeHealthSync = onHealthDataUpdated(loadSummary, { debounceMs: 250 });
    return () => {
      mounted = false;
      unsubscribeHealthSync();
    };
  }, [fetchHealthSummary, getCachedHealthSummary]);

  const latestVitals = metrics?.vitals?.latest || summary?.vitals || {};
  const latestAssessment = Array.isArray(assessments) && assessments.length > 0
    ? [...assessments].sort((a, b) => new Date(b?.createdAt || 0) - new Date(a?.createdAt || 0))[0]
    : (summary?.latestAssessment || null);

  useEffect(() => {
    let mounted = true;

    const loadTrend = async () => {
      try {
        const days = timeRange === "weekly" ? 14 : 30;
        const targetPoints = timeRange === "weekly" ? 7 : 6;
        const res = await fetchVitalsMetrics(days);
        const list = Array.isArray(res?.vitals) ? [...res.vitals].sort((a, b) => new Date(a.time) - new Date(b.time)) : [];

        const scored = list.map((row) => deriveQureoScore({
          summaryScore: null,
          assessmentScore: latestAssessment?.score,
          bloodPressure: row?.bloodPressure,
          heartRate: row?.heartRate,
          oxygenLevel: row?.oxygenLevel,
          temperature: row?.temperature,
        })).filter((value) => Number.isFinite(value));

        if (!mounted) return;

        if (!scored.length) {
          const fallback = Array.from({ length: targetPoints }, (_, index) => {
            const variance = Math.sin(index) * 2;
            return Math.max(0, Math.min(100, Math.round((summary?.healthScore?.total ?? 0) + variance)));
          });
          setTrendPoints(fallback);
          setTrendDelta(0);
          return;
        }

        const bucketSize = Math.max(1, Math.ceil(scored.length / targetPoints));
        const buckets = [];
        for (let i = 0; i < scored.length; i += bucketSize) {
          const chunk = scored.slice(i, i + bucketSize);
          const avg = average(chunk);
          if (avg != null) buckets.push(Math.round(avg));
        }
        const normalized = buckets.slice(-targetPoints);

        const half = Math.max(1, Math.floor(normalized.length / 2));
        const first = average(normalized.slice(0, half));
        const second = average(normalized.slice(half));
        const delta = Number.isFinite(first) && first > 0 && Number.isFinite(second)
          ? ((second - first) / first) * 100
          : 0;

        setTrendPoints(normalized);
        setTrendDelta(delta);
      } catch {
        if (!mounted) return;
        setTrendPoints([]);
        setTrendDelta(0);
      }
    };

    loadTrend();
    return () => {
      mounted = false;
    };
  }, [fetchVitalsMetrics, latestAssessment?.score, summary?.healthScore?.total, timeRange]);

  const score = deriveQureoScore({
    summaryScore: summary?.healthScore?.total,
    assessmentScore: latestAssessment?.score,
    bloodPressure: latestVitals?.bloodPressure ?? vitals?.bp,
    heartRate: latestVitals?.heartRate ?? vitals?.heart,
    oxygenLevel: latestVitals?.oxygenLevel ?? vitals?.oxygen,
    temperature: latestVitals?.temperature ?? vitals?.temp,
  }) ?? 0;

  const scoreLabel = getQureoScoreLabel(score);
  const components = summary?.healthScore?.components || {};
  const overviewRows = useMemo(() => getOverviewRows(components), [components]);
  const lastUpdatedLabel = formatRelativeTime(summary?.lastUpdated || latestAssessment?.updatedAt);
  const chart = useMemo(() => buildChartPath(trendPoints), [trendPoints]);
  const trendUp = trendDelta >= 0;
  const trendColor = trendUp ? PRIMARY_COLOR : "#ef4444";
  const trendIcon = trendUp ? <TrendingUp size={12} /> : <TrendingDown size={12} />;
  const trendLabel = `${trendUp ? "+" : ""}${Math.abs(trendDelta).toFixed(1)}% vs previous ${timeRange === "weekly" ? "week" : "period"}`;
  const headlineText = score >= 75
    ? "You are a very healthy individual. There's still room for improvement."
    : score >= 45
      ? "Your health is stable, but a few habits need improvement for better outcomes."
      : "Your score indicates risk. Consider immediate follow-up and healthier daily habits.";

  return (
    <div
      style={{
        minHeight: "100vh",
        background: "#f3f0f8",
        padding: "12px 12px 110px",
      }}
    >
      <div
        style={{
          width: "100%",
          maxWidth: 720,
          margin: "0 auto",
          background: "#eceeef",
          borderRadius: 20,
          padding: "10px 10px 14px",
        }}
      >
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 2 }}>
        <button
          type="button"
          onClick={() => navigate(-1)}
          style={{
            border: "none",
            background: "transparent",
            padding: 0,
            display: "inline-flex",
            alignItems: "center",
            cursor: "pointer",
            color: "#2d3640",
          }}
          aria-label="Go back"
        >
          <ArrowLeft size={18} />
        </button>
        <div style={{ fontSize: 13, fontWeight: 600, color: "#2d3640" }}>Qureo Score</div>
        <CircleHelp size={14} color="#8e9aa4" />
      </div>

      <DottedScoreRing score={score} />

      <div style={{ fontSize: 9, color: "#8f99a2", textAlign: "center", marginTop: -1, display: "flex", alignItems: "center", justifyContent: "center", gap: 4 }}>
        <Clock3 size={10} color="#8f99a2" />
        <span>{lastUpdatedLabel}</span>
      </div>

      <div style={{ textAlign: "center", marginTop: 10, marginBottom: 10, color: "#3f4752", fontSize: 14, lineHeight: 1.4 }}>
        {headlineText}
      </div>

      <div style={{ display: "grid", gap: 7, marginBottom: 12 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", fontSize: 12 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <span style={{ width: 6, height: 6, borderRadius: 999, background: PRIMARY_COLOR }} />
            <span style={{ color: "#25313b" }}>75 - 100</span>
          </div>
          <div style={{ color: "#111827", fontWeight: 600 }}>Optimal Health</div>
        </div>

        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", fontSize: 12 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <span style={{ width: 6, height: 6, borderRadius: 999, background: "#f59e0b" }} />
            <span style={{ color: "#25313b" }}>40 - 74</span>
          </div>
          <div style={{ color: "#111827", fontWeight: 600, display: "flex", alignItems: "center", gap: 4 }}>
            Average Health
            <CircleHelp size={12} color="#9ca3af" />
          </div>
        </div>

        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", fontSize: 12 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <span style={{ width: 6, height: 6, borderRadius: 999, background: "#ef4444" }} />
            <span style={{ color: "#25313b" }}>0 - 39</span>
          </div>
          <div style={{ color: "#111827", fontWeight: 600, display: "flex", alignItems: "center", gap: 4 }}>
            Bad Health
            <CircleHelp size={12} color="#9ca3af" />
          </div>
        </div>
      </div>

      <div style={{ color: "#25313b", fontSize: 13, fontWeight: 600, marginBottom: 6 }}>Overview</div>

      <div
        style={{
          border: "1px solid #dde3e8",
          borderRadius: 14,
          background: "#f2f4f6",
          padding: "10px 10px 4px",
          marginBottom: 14,
        }}
      >
        <div style={{ fontSize: 10, color: "#7a8590", marginBottom: 10, lineHeight: 1.45 }}>
          Your Qureo Score reflects your overall health, including lifestyle factors like nutrition, exercise, and mental wellness.
        </div>

        {overviewRows.map((row) => (
          <div
            key={row.title}
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              borderTop: "1px solid #e2e8f0",
              padding: "8px 2px",
            }}
          >
            <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
              <span>{row.icon}</span>
              <div>
                <div style={{ fontSize: 12, fontWeight: 600, color: "#1f2937" }}>{row.title}</div>
                <div style={{ fontSize: 10, color: "#7a8590", marginTop: 1 }}>{row.subtitle}</div>
              </div>
            </div>

            <div style={{ display: "flex", alignItems: "center", gap: 5, color: row.statusColor, fontSize: 11, fontWeight: 600 }}>
              <span style={{ width: 5, height: 5, borderRadius: 99, background: row.statusColor }} />
              {row.status}
            </div>
          </div>
        ))}
      </div>

      <div style={{ color: "#25313b", fontSize: 13, fontWeight: 600, marginBottom: 6 }}>Health Score Trend</div>

      <div
        style={{
          borderRadius: 14,
          border: "1px solid #dde3e8",
          background: "#f2f4f6",
          padding: 11,
        }}
      >
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
          <div>
            <div style={{ fontSize: 31, fontWeight: 700, lineHeight: 1, color: "#111827" }}>{Number(score).toFixed(1)}pts</div>
            <div style={{ fontSize: 11, color: trendColor, marginTop: 2, display: "flex", alignItems: "center", gap: 4 }}>
              {trendIcon}
              <span>{trendLabel}</span>
            </div>
          </div>

          <button
            type="button"
            onClick={() => setTimeRange((prev) => (prev === "weekly" ? "monthly" : "weekly"))}
            style={{
              fontSize: 12,
              color: PRIMARY_COLOR,
              fontWeight: 600,
              display: "flex",
              alignItems: "center",
              gap: 4,
              border: "none",
              background: "transparent",
              padding: 0,
              cursor: "pointer",
            }}
          >
            <Calendar size={13} />
            {timeRange === "weekly" ? "Weekly" : "Monthly"}
            <ChevronDown size={12} />
          </button>
        </div>

        <div style={{ width: "100%", height: 92, borderRadius: 10, background: "#edf1f4", padding: 8 }}>
          <svg viewBox="0 0 320 76" width="100%" height="100%" preserveAspectRatio="none">
            <defs>
              <linearGradient id="trendFill" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor={PRIMARY_COLOR} stopOpacity="0.22" />
                <stop offset="100%" stopColor={PRIMARY_COLOR} stopOpacity="0" />
              </linearGradient>
              <filter id="trendGlow" x="-10%" y="-30%" width="120%" height="180%">
                <feGaussianBlur stdDeviation="1.2" result="blur" />
                <feMerge>
                  <feMergeNode in="blur" />
                  <feMergeNode in="SourceGraphic" />
                </feMerge>
              </filter>
            </defs>

            <path d={chart.areaPath} fill="url(#trendFill)" />
            <path d={chart.linePath} fill="none" stroke={PRIMARY_COLOR} strokeWidth="3.25" strokeLinecap="round" filter="url(#trendGlow)" />
          </svg>
        </div>

        <div style={{ marginTop: 8, fontSize: 12, color: "#6b7280" }}>{scoreLabel}</div>
      </div>
      </div>
    </div>
  );
};

export default HealthDashboardSections;
